<template>
  <div>
    <v-container
      class="d-flex flex-column ga-4"
      fluid
    >
      <div
        v-if="store.state.features.enable_provider_licenses"
        id="my-work"
      >
        <StandardCollapseCard title="My Work">
          <StandardPageTemplate
            :margin="0"
            container-class="px-0 py-0 bg-white"
            no-title
          >
            <DataTable
              @click:row="handleTaskRowClick"
              :headers="myWorkTableHeaders"
              :items="tasks"
              :items-per-page="10"
              :loading="myWorkLoading"
              class="b-radius-0 b-1 bc-outlined-gray"
              no-data-text="No open tasks"
              fixed-header
              hover
            >
              <template #[`item.workflow_name`]="{ item: task }">
                <router-link
                  @click.stop
                  :to="{
                    name: 'WorkflowInstanceShow',
                    params: { workflowInstanceId: task.workflow_instance_id },
                  }"
                  class="text-primary"
                >
                  {{ task.workflow_name }}
                </router-link>
              </template>

              <template #[`item.agreement_status`]="{ item: task }">
                <StandardChip
                  v-if="resolveAgreementTaskStatus(task)"
                  :data-testid="`agreement-status-${task.id}`"
                  :text="resolveAgreementTaskStatus(task)"
                  :text-translate="false"
                  role="status"
                />
              </template>

              <template #[`item.actions`]="{ item: task }">
                <StandardButton
                  v-if="isAgreementWorkflowTask(task)"
                  @click.stop="openAgreementDocument(task)"
                  :data-testid="`agreement-action-${task.id}`"
                  :text="resolveAgreementTaskActionLabel(task)"
                  role="secondary"
                  variant="outlined"
                />
              </template>

              <template #[`item.created_at`]="{ item: task }">
                <LongDate :date="task.created_at" />
              </template>

              <template #[`item.status`]="{ item: task }">
                <StandardChip
                  :data-testid="`task-status-${task.id}`"
                  :text="task.status"
                  role="status"
                  capitalize
                />
              </template>
            </DataTable>
          </StandardPageTemplate>
        </StandardCollapseCard>
      </div>

      <StandardCard title="Licenses">
        <StandardPageTemplate
          @create="handleCreate"
          :margin="0"
          :search="{
            modelValue: query,
            updateHandler: handleSearchUpdate,
            label: 'Search licenses',
          }"
          container-class="px-0 py-0 bg-white"
          resource-name="license"
          createable
          no-title
        >
          <DataTableServer
            @click:row="handleRowClick"
            @update:page="handlePageChange"
            :headers="tableHeaders"
            :items="licenses"
            :items-length="totalItemCount"
            :items-per-page="pageSize"
            :loading="processing"
            class="b-radius-0 b-1 bc-outlined-gray"
            no-data-text="No licenses found"
            fixed-header
            hover
          >
            <template #[`item.name`]="{ item: license }">
              {{ $bct(license.name) }}
            </template>

            <template #[`item.start_date`]="{ item: license }">
              <LongDate :date="license.start_date" />
            </template>

            <template #[`item.end_date`]="{ item: license }">
              <LongDate :date="license.end_date" />
            </template>
          </DataTableServer>
        </StandardPageTemplate>
      </StandardCard>
    </v-container>
  </div>

  <ResourceDialog
    ref="createDialog"
    title="Add license"
    hide-save-button
  >
    <template #form>
      <v-row dense>
        <LabeledAutocomplete
          v-model="selectedLicensingProgramId"
          :items="licensingPrograms"
          item-title="name"
          item-value="id"
          message="Licensing program"
          clearable
        />
      </v-row>
    </template>

    <template #actions>
      <StandardButton
        @click="createDialog.close()"
        role="secondary"
        text="Cancel"
      />

      <StandardButton
        @click="startApplication"
        :disabled-text="selectedLicensingProgramId ? undefined : 'Select a licensing program'"
        :loading="processing"
        text="Start application"
      />
    </template>
  </ResourceDialog>

  <TaskDetailDialog
    v-if="selectedTask"
    @close="closeTaskDetail"
    @updated="handleTaskUpdated"
    :open="taskDetailOpen"
    :task="selectedTask"
  />
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import {
  agreementWorkflowTaskActionLabel as agreementTaskActionLabel,
  agreementWorkflowTaskStatus as agreementTaskStatus,
  isAgreementWorkflowTask,
} from '@/shared/services/agreement-workflow-task';
import DataTable from '@/shared/components/table/DataTable.vue';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import TaskDetailDialog from '@/shared/components/tasks/TaskDetailDialog.vue';
import useApi from '@/shared/composables/useApi';

const $bct = inject('translator');

const { api: Api } = useApi();
const route = useRoute();
const router = useRouter();
const store = useStore();

const createDialog = ref(null);
const licenses = ref([]);
const licensingPrograms = ref([]);
const selectedLicensingProgramId = ref(null);
const page = ref(1);
const pageSize = ref(100);
const processing = ref(true);
const query = ref(null);
const searchCounter = ref(0);
const totalItemCount = ref(0);
const tableHeaders = ref([
  { title: 'Provider', value: 'provider_name' },
  { title: 'ID', value: 'name' },
  { title: 'Number', value: 'number' },
  { title: 'Status', value: 'status' },
  { title: 'Start date', value: 'start_date' },
  { title: 'End date', value: 'end_date' },
]);

const myWorkLoading = ref(true);
const myWorkBaseTableHeaders = [
  { title: 'Name', value: 'name' },
  { title: 'Description', value: 'description' },
  { title: 'Workflow', value: 'workflow_name' },
  { title: 'Status', value: 'status', align: 'center' },
  { title: 'Created', value: 'created_at', align: 'end' },
];
const myWorkAgreementTableHeaders = [
  { title: 'Agreement', value: 'agreement_status', align: 'center' },
  { title: '', value: 'actions', align: 'end', sortable: false },
];
const selectedTask = ref(null);
const taskDetailOpen = ref(false);
const tasks = ref([]);

const hasAgreementWorkflowTasks = computed(() => tasks.value.some(isAgreementWorkflowTask));

const myWorkTableHeaders = computed(() => {
  if (!hasAgreementWorkflowTasks.value) {
    return myWorkBaseTableHeaders;
  }

  const statusIndex = myWorkBaseTableHeaders.findIndex((header) => header.value === 'status');

  return [
    ...myWorkBaseTableHeaders.slice(0, statusIndex),
    myWorkAgreementTableHeaders[0],
    ...myWorkBaseTableHeaders.slice(statusIndex),
    myWorkAgreementTableHeaders[1],
  ];
});

watch(
  () => route.hash,
  () => {
    void nextTick(() => {
      scrollToMyWorkIfHash();
    });
  },
);

function handleCreate() {
  createDialog.value.open();
}

async function handleRowClick(_event, { item }) {
  await router.push({
    name: 'LicenseShow',
    params: { id: item.id },
  });
}

async function startApplication() {
  processing.value = true;

  const response = await Api.manager.license.create({
    licensing_program_id: selectedLicensingProgramId.value,
  });

  if (!response?.data) {
    processing.value = false;
    return;
  }

  createDialog.value.close();

  const workflowInstanceId = response.data.workflow_instance_id;

  try {
    if (workflowInstanceId) {
      await router.push({
        name: 'WorkflowInstanceShow',
        params: { workflowInstanceId },
      });
    } else {
      await router.push({
        name: 'LicenseFormNew',
        params: {
          licenseId: response.data.id,
          formId: response.data.form_id,
        },
      });
    }
  } catch {
    processing.value = false;
  }
}

function handlePageChange(newPage) {
  page.value = newPage;
  void baseLoad();
}

function handleSearchUpdate(newValue) {
  query.value = newValue;
  page.value = 1;
  void baseLoad();
}

async function baseLoad() {
  processing.value = true;

  searchCounter.value += 1;
  const currentSearchCounter = searchCounter.value;

  const response = await Api.manager.license.index({
    page: page.value,
    page_size: pageSize.value,
    query: query.value,
  });
  if (searchCounter.value !== currentSearchCounter) return;

  licenses.value = response?.data || [];
  totalItemCount.value = parseInt(response?.headers['x-total-count'] || 0, 10);
  pageSize.value = parseInt(response?.headers['x-page-size'] || 100, 10);

  processing.value = false;
}

async function loadLicensingPrograms() {
  const response = await Api.manager.licensingProgram.index();
  licensingPrograms.value = response?.data || [];
}

async function loadMyWorkTasks() {
  myWorkLoading.value = true;

  const response = await Api.manager.workflowTask.index();
  tasks.value = response?.data || [];
  myWorkLoading.value = false;
}

function resolveAgreementTaskStatus(task) {
  return agreementTaskStatus(task, store.state.profile?.id);
}

function resolveAgreementTaskActionLabel(task) {
  return agreementTaskActionLabel(task, store.state.profile?.id);
}

async function openAgreementDocument(task) {
  const agreementId = task.workflow_artifact?.artifact_id;

  await router.push({
    name: 'DocumentIndex',
    ...(agreementId ? { query: { agreementId } } : {}),
  });
}

function handleTaskRowClick(_event, { item }) {
  if (isAgreementWorkflowTask(item)) {
    void openAgreementDocument(item);
    return;
  }

  selectedTask.value = item;
  taskDetailOpen.value = true;
}

function closeTaskDetail() {
  taskDetailOpen.value = false;
  selectedTask.value = null;
}

async function handleTaskUpdated() {
  closeTaskDetail();
  await loadMyWorkTasks();
}

function scrollToMyWorkIfHash() {
  if (route.hash !== '#my-work') {
    return;
  }

  document.getElementById('my-work')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function load() {
  await baseLoad();
  void loadLicensingPrograms();

  if (store.state.features.enable_provider_licenses) {
    await loadMyWorkTasks();
  } else {
    myWorkLoading.value = false;
  }

  await nextTick();
  scrollToMyWorkIfHash();
}

onMounted(load);
</script>
